import React, { useEffect, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import client from '../../api/client';
import { useToast } from '../../hooks/use-toast';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Users, CheckCircle2, XCircle, Clock, Mail, MailX, MessageSquare, MessageSquareX, MapPin, UtensilsCrossed, Eye, AlertTriangle, Loader2, Send, UserPlus, ChevronRight, Plug, Cloud, KeyRound, Wifi, WifiOff, Check, Copy } from 'lucide-react';

const COLORS = ['#7B2C3B', '#B8896C', '#4A7A4A', '#3E5C76', '#9A6E4C', '#C9A97A'];

export default function Insights({ onNavigate }) {
  const [data, setData] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);  // { title, ids, canNudge, nudgeKind }
  const [nudging, setNudging] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const [stats, gs] = await Promise.all([
          client.get('/dashboard/wedding-stats'),
          client.get('/guests'),
        ]);
        setData(stats.data);
        setGuests(gs.data || []);
      } catch (e) {
        toast({ title: 'Could not load insights', description: e?.response?.data?.detail || e.message });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !data) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#7B2C3B]" /></div>;
  }

  const rsvpPie = [
    { name: 'Yes', value: data.rsvp.yes },
    { name: 'No', value: data.rsvp.no },
    { name: 'Pending', value: data.rsvp.pending },
  ].filter((r) => r.value > 0);

  const emailBar = [
    { name: 'Delivered', value: data.email.delivered, fill: '#4A7A4A' },
    { name: 'Opened', value: data.email.opened, fill: '#3E5C76' },
    { name: 'Bounced', value: data.email.bounced, fill: '#9A6E4C' },
    { name: 'Failed', value: data.email.failed, fill: '#B84A4A' },
  ];

  const guestsById = Object.fromEntries(guests.map((g) => [g.id, g]));

  const openDrill = (title, ids, canNudge = false, nudgeKind = 'rsvp') => setDrill({ title, ids, canNudge, nudgeKind });

  const reloadStats = async () => {
    try {
      const [stats, gs] = await Promise.all([
        client.get('/dashboard/wedding-stats'),
        client.get('/guests'),
      ]);
      setData(stats.data);
      setGuests(gs.data || []);
    } catch (e) {
      // silent — user can retry
    }
  };

  const sendNudge = async () => {
    if (!drill) return;
    const withPhone = drill.ids.filter((id) => (guestsById[id]?.phone || '').trim());
    if (!withPhone.length) {
      toast({ title: 'No phone numbers', description: 'None of these guests have a phone number on file.' });
      return;
    }
    const kind = drill.nudgeKind || 'rsvp';
    const label = kind === 'plus-one-name' ? 'plus-one name reminder' : 'nudge';
    if (!window.confirm(`Send a friendly ${label} SMS to ${withPhone.length} guest${withPhone.length === 1 ? '' : 's'} now?`)) return;
    const endpoint = kind === 'plus-one-name'
      ? '/dashboard/nudge-plus-one-names'
      : '/dashboard/nudge-non-repliers';
    setNudging(true);
    try {
      const { data: r } = await client.post(endpoint, { guest_ids: withPhone, force: kind === 'plus-one-name' });
      toast({
        title: `${label.charAt(0).toUpperCase() + label.slice(1)} sent to ${r.sent} guest${r.sent === 1 ? '' : 's'}`,
        description: [
          r.skipped_no_phone ? `${r.skipped_no_phone} skipped (no phone)` : null,
          r.skipped_no_rsvp_token ? `${r.skipped_no_rsvp_token} skipped (no RSVP link yet)` : null,
          r.skipped_throttled ? `${r.skipped_throttled} skipped (already nudged this week)` : null,
          r.failures?.length ? `${r.failures.length} failed` : null,
        ].filter(Boolean).join(' · ') || 'All good!',
      });
      setDrill(null);
      await reloadStats();
    } catch (e) {
      toast({ title: 'Nudge failed', description: e?.response?.data?.detail || e.message });
    } finally {
      setNudging(false);
    }
  };

  return (
    <div data-testid="wedding-insights" className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl text-[#2A2A2A] flex items-center gap-3"><AppIcon feature="insights" size="lg" reveal testId="section-icon-insights" />Wedding dashboard</h1>
        <p className="text-sm text-[#6A6A6A] mt-1">Key numbers, delivery health, and the guests who need a nudge.</p>
      </div>

      {/* Top KPI cards — each shows PEOPLE (headcount) as the primary number
          with the underlying invite count as sub-label. All four are drillable. */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          testId="kpi-invited" tone="wine" icon={Users}
          label="Total invited"
          value={data.headcount.totalInvited}
          sub={`${data.headcount.totalInvited} people · ${data.headcount.invitesTotal} invite${data.headcount.invitesTotal === 1 ? '' : 's'}`}
          onClick={() => openDrill(`All ${data.headcount.invitesTotal} invited guests · ${data.headcount.totalInvited} headcount`, data.drilldown.invitedGuestIds)}
        />
        <KpiCard
          testId="kpi-attending" tone="green" icon={CheckCircle2}
          label="Attending"
          value={data.headcount.attending}
          sub={`${data.headcount.attending} people · ${data.headcount.invitesYes} invite${data.headcount.invitesYes === 1 ? '' : 's'} said yes`}
          onClick={() => openDrill(`Attending · ${data.headcount.attending} people from ${data.headcount.invitesYes} invites`, data.drilldown.attendingGuestIds)}
        />
        <KpiCard
          testId="kpi-declined" tone="stone" icon={XCircle}
          label="Declined"
          value={data.headcount.declined}
          sub={`${data.headcount.declined} people · ${data.headcount.invitesNo} invite${data.headcount.invitesNo === 1 ? '' : 's'} sent regrets`}
          onClick={() => openDrill(`Declined · ${data.headcount.invitesNo} invites`, data.drilldown.declinedGuestIds)}
        />
        <KpiCard
          testId="kpi-pending" tone="ochre" icon={Clock}
          label="Awaiting reply"
          value={data.headcount.pending}
          sub={data.headcount.pendingPotentialMax !== data.headcount.pending
            ? `${data.headcount.pending} invite${data.headcount.pending === 1 ? '' : 's'} · up to ${data.headcount.pendingPotentialMax} more people`
            : `${data.headcount.pending} invite${data.headcount.pending === 1 ? '' : 's'} haven't replied`}
          onClick={() => openDrill('Guests awaiting reply', data.drilldown.pendingGuestIds, true)}
        />
      </div>

      {/* Data quality row */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
        <KpiCard testId="kpi-missing-email" tone="ochre" icon={MailX} label="Missing email" value={data.data.missingEmail} sub="Click to see who" onClick={() => openDrill('Guests with no email', data.drilldown.missingEmailGuestIds)} />
        <KpiCard testId="kpi-missing-phone" tone="ochre" icon={MessageSquareX} label="Missing phone" value={data.data.missingPhone} sub="Click to see who" onClick={() => openDrill('Guests with no phone', data.drilldown.missingPhoneGuestIds)} />
        <KpiCard testId="kpi-missing-plusone-names" tone="ochre" icon={UserPlus} label="Missing +1 names" value={data.data.missingPlusOneNames || 0} sub="Yes RSVPs w/ nameless plus-one" onClick={() => openDrill('Missing plus-one names', data.drilldown.missingPlusOneNameGuestIds || [], true, 'plus-one-name')} />
        <KpiCard testId="kpi-email-failed" tone="danger" icon={AlertTriangle} label="Email failed / bounced" value={data.email.bounced + data.email.failed} sub="Bad address — needs fixing" onClick={() => openDrill('Failed email sends', data.drilldown.failedEmailGuestIds)} />
        <KpiCard testId="kpi-sms-failed" tone="danger" icon={AlertTriangle} label="SMS failed" value={data.sms.failed} sub="Undeliverable numbers" onClick={() => openDrill('Failed SMS sends', data.drilldown.failedSmsGuestIds)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* RSVP donut */}
        <div className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
          <h3 className="font-serif text-xl text-[#2A2A2A] mb-3">RSVP breakdown</h3>
          {rsvpPie.length === 0 ? (
            <p className="text-sm text-[#9A8A7E] py-10 text-center">No RSVPs yet — send invitations to see this fill in.</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={rsvpPie} innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" nameKey="name" label={(e) => `${e.name}: ${e.value}`}>
                    {rsvpPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Opened but not replied */}
        <div className="soft-card bg-gradient-to-br from-[#F4E4DA] to-[#FBF7F2] rounded-2xl border border-[#EBD3C2] p-5">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="w-4 h-4 text-[#7B2C3B]" />
            <h3 className="font-serif text-xl text-[#2A2A2A]">Opened but haven't replied</h3>
          </div>
          {(() => {
            // Translate the "opened-not-replied" invite count into a people
            // headcount by adding each guest's eligible plus-one.
            const openedIds = new Set(data.drilldown.openedNotRepliedGuestIds || []);
            const invites = data.email.openedNotReplied;
            const head = guests.reduce((acc, g) => {
              if (!openedIds.has(g.id)) return acc;
              const po = (g.plusOneComing === null || g.plusOneComing === undefined) ? !!g.plusOne : !!g.plusOneComing;
              return acc + 1 + (po ? 1 : 0);
            }, 0);
            return (
              <>
                <p className="text-4xl font-serif text-[#7B2C3B] my-2" data-testid="stat-opened-not-replied">{invites}</p>
                <p className="text-[11px] text-[#7B2C3B] -mt-1 mb-1">
                  {invites} invite{invites === 1 ? '' : 's'} · {head} people (incl. plus-ones)
                </p>
                <p className="text-sm text-[#6A6A6A]">These guests saw your invitation but haven't RSVPed. Consider a friendly nudge SMS.</p>
              </>
            );
          })()}
          {data.email.openedNotReplied > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                data-testid="drill-opened-not-replied"
                onClick={() => openDrill("Opened, awaiting RSVP", data.drilldown.openedNotRepliedGuestIds, true)}
                className="text-sm text-[#7B2C3B] hover:underline"
              >
                See the {data.email.openedNotReplied} guest{data.email.openedNotReplied === 1 ? '' : 's'} →
              </button>
              <AutoNudgeButton onNudged={reloadStats} />
            </div>
          )}
          {data.email.openedNotReplied === 0 && data.rsvp.pending > 0 && (
            <div className="mt-3">
              <AutoNudgeButton onNudged={reloadStats} />
            </div>
          )}
        </div>
      </div>

      {/* Email health bar chart + Meals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-[#7B2C3B]" />
            <h3 className="font-serif text-xl text-[#2A2A2A]">Email delivery</h3>
            <span className="ml-auto text-xs text-[#9A8A7E]">Sent: {data.email.sent}</span>
          </div>
          {data.email.webhookLastAt && (
            <p data-testid="webhook-heartbeat" className="text-[11px] text-emerald-700 mb-3 inline-flex items-center gap-1">
              <Check className="w-3 h-3" /> Delivery tracking connected · last update {new Date(data.email.webhookLastAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}
            </p>
          )}
          {data.email.sent > 0
            && !data.email.webhookLastAt
            && (data.email.delivered + data.email.opened + data.email.bounced + data.email.failed === 0) ? (
            <PostmarkWebhookHint />
          ) : (
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={emailBar} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12, fill: '#3A3A3A' }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
          <div className="flex items-center gap-2 mb-3">
            <UtensilsCrossed className="w-4 h-4 text-[#7B2C3B]" />
            <h3 className="font-serif text-xl text-[#2A2A2A]">Meal choices (among Yes)</h3>
          </div>
          {data.meals.length === 0 ? (
            <p className="text-sm text-[#9A8A7E] py-10 text-center">No meal choices submitted yet.</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer>
                <BarChart data={data.meals}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#3A3A3A' }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#3A3A3A' }} axisLine={false} tickLine={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#B8896C" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Side breakdown */}
      <div className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-4 h-4 text-[#7B2C3B]" />
          <h3 className="font-serif text-xl text-[#2A2A2A]">Sides (incl. plus-ones)</h3>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {data.sides.map((s, i) => (
            <div key={s.name} className="text-center py-4 rounded-xl bg-[#FBF7F2] border border-[#EFE3D8]">
              <p className="text-xs uppercase tracking-wider text-[#9A8A7E]">{s.name}</p>
              <p className="font-serif text-3xl mt-1" style={{ color: COLORS[i % COLORS.length] }}>{s.count}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Integration partners health */}
      <IntegrationHealthPanel />

      {/* Drilldown modal */}
      {drill && (
        <div
          data-testid="drill-modal"
          onClick={() => setDrill(null)}
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl border border-[#EFE3D8] max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-[#EFE3D8] flex items-center justify-between">
              <h3 className="font-serif text-xl text-[#2A2A2A]">{drill.title}</h3>
              <button data-testid="drill-close" onClick={() => setDrill(null)} className="text-sm text-[#7B2C3B]">Close</button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {drill.ids.length === 0 ? (
                <p className="text-sm text-[#9A8A7E]">Nobody here — clean data!</p>
              ) : (
                <ul className="divide-y divide-[#EFE3D8]">
                  {drill.ids.map((id) => {
                    const g = guestsById[id];
                    if (!g) return null;
                    const jump = () => {
                      setDrill(null);
                      if (onNavigate) onNavigate('guests', { edit: id });
                    };
                    return (
                      <li key={id} className="py-2">
                        <button
                          type="button"
                          data-testid={`drill-guest-${id}`}
                          onClick={jump}
                          className="w-full text-left group flex items-center justify-between gap-2 rounded-lg px-2 -mx-2 py-1 hover:bg-[#F4E4DA]/50 transition-colors"
                          title="Open this guest's record"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-[#2A2A2A] group-hover:text-[#7B2C3B] truncate">{g.name}</p>
                            <p className="text-[11px] text-[#9A8A7E] truncate">{g.email || 'no email'}{g.phone ? ` · ${g.phone}` : ''}{g.rsvp ? ` · ${g.rsvp}` : ''}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#B8896C] group-hover:text-[#7B2C3B] shrink-0" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {drill.canNudge && drill.ids.length > 0 && (
              <div className="p-4 border-t border-[#EFE3D8] bg-[#FBF7F2]">
                {(() => {
                  const withPhone = drill.ids.filter((id) => (guestsById[id]?.phone || '').trim()).length;
                  const withoutPhone = drill.ids.length - withPhone;
                  const isPlusOne = drill.nudgeKind === 'plus-one-name';
                  const smsLabel = isPlusOne ? 'plus-one name reminder SMS' : 'nudge SMS';
                  return (
                    <>
                      <p className="text-[11px] text-[#6A6A6A] mb-2">
                        <Send className="inline w-3 h-3 mr-1 -mt-0.5" />
                        Sends the {smsLabel} (editable on the RSVP tab) to everyone here who has a phone number
                        {withoutPhone > 0 && <span> · {withoutPhone} without phone will be skipped</span>}.
                      </p>
                      <button
                        data-testid="drill-nudge-btn"
                        onClick={sendNudge}
                        disabled={nudging || withPhone === 0}
                        className="w-full btn-primary py-2 rounded-full text-sm font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2"
                      >
                        {nudging ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send {smsLabel} to {withPhone} guest{withPhone === 1 ? '' : 's'}</>}
                      </button>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// PostmarkWebhookHint — shown inside the Email delivery card whenever we've
// sent emails but Postmark hasn't fired ANY delivery webhook back. Almost
// always means the tracking webhook URL hasn't been pasted into the couple's
// Postmark server yet. Gives them the exact URL + one-tap copy.
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// AutoNudgeButton — one-tap SMS reminder to every guest who opened the
// invitation but hasn't RSVPed yet. Falls back to "emailed + still pending"
// when Postmark webhook isn't wired.
// -----------------------------------------------------------------------------
function AutoNudgeButton({ onNudged }) {
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [confirming, setConfirming] = React.useState(false);
  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const { data } = await client.post('/invitations/nudge-non-repliers');
      setResult(data);
      onNudged?.();
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message);
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };
  if (result) {
    return (
      <span data-testid="auto-nudge-result" className="inline-flex items-center gap-1 text-xs text-emerald-700">
        <Check className="w-3.5 h-3.5" /> Sent {result.count} nudge{result.count === 1 ? '' : 's'}
      </span>
    );
  }
  if (error) {
    return (
      <span data-testid="auto-nudge-error" className="inline-flex items-center gap-1 text-xs text-[#7B2C3B]">
        <AlertTriangle className="w-3.5 h-3.5" /> {error}
      </span>
    );
  }
  if (confirming) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs">
        <span className="text-[#4A4A4A]">Send SMS nudge to all opened-but-pending guests?</span>
        <button
          data-testid="auto-nudge-confirm"
          onClick={send}
          disabled={sending}
          className="px-2.5 py-1 rounded-full bg-[#7B2C3B] text-white disabled:opacity-60 inline-flex items-center gap-1"
        >
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
          Yes, send
        </button>
        <button onClick={() => setConfirming(false)} className="px-2 py-1 text-[#6A6A6A] hover:underline">Cancel</button>
      </span>
    );
  }
  return (
    <button
      data-testid="auto-nudge-btn"
      onClick={() => setConfirming(true)}
      className="text-xs px-3 py-1.5 rounded-full bg-[#7B2C3B] text-white hover:bg-[#611E2E] inline-flex items-center gap-1"
    >
      <Send className="w-3 h-3" /> Auto-Nudge SMS
    </button>
  );
}

function PostmarkWebhookHint() {
  const [copied, setCopied] = React.useState(false);
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/invitations/postmark-webhook`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <div data-testid="postmark-webhook-hint" className="rounded-xl border border-[#EFC9B0] bg-[#FFF7EC] p-4 text-xs text-[#7B4A1A]">
      <p className="font-medium inline-flex items-center gap-1.5 text-sm text-[#7B2C3B]">
        <AlertTriangle className="w-4 h-4" />
        Delivery tracking isn&apos;t reporting back yet
      </p>
      <p className="mt-2 text-[#5C4A32]">
        Your invitation emails <strong>went out</strong> (see the little dots next to each guest — cream means &ldquo;queued&rdquo;),
        but Postmark hasn&apos;t told us whether they landed, got opened, or bounced. To fix, paste this webhook URL into your Postmark server:
      </p>
      <div className="mt-3 flex items-center gap-2">
        <code data-testid="postmark-webhook-url" className="flex-1 text-[11px] bg-white border border-[#EFC9B0] rounded px-2 py-1.5 truncate text-[#2A2A2A]">
          {webhookUrl}
        </code>
        <button
          data-testid="copy-postmark-webhook"
          onClick={copy}
          className="text-[11px] px-2.5 py-1.5 rounded-full bg-[#7B2C3B] text-white hover:bg-[#611E2E] inline-flex items-center gap-1"
        >
          {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
        </button>
      </div>
      <ol className="mt-3 ml-4 list-decimal space-y-1 text-[#5C4A32]">
        <li>Open <a href="https://account.postmarkapp.com/servers" target="_blank" rel="noreferrer" className="text-[#7B2C3B] underline">Postmark</a> → your server → <em>Message Streams</em> → <em>outbound</em> → <em>Webhooks</em>.</li>
        <li>Click <em>Add webhook</em>, paste the URL above.</li>
        <li>Tick <em>Delivery</em>, <em>Bounce</em>, <em>Spam Complaint</em>, and <em>Open</em>. Save.</li>
        <li>Send yourself a test invite from the Invitations tab — this chart fills in within a minute.</li>
      </ol>
      <p className="mt-2 text-[11px] italic">
        Meanwhile, guests are still getting your invitations — you just can&apos;t see the delivery details here yet.
      </p>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = 'wine', onClick, testId }) {
  const tones = {
    wine: 'bg-gradient-to-br from-[#7B2C3B] to-[#5C1F2B] text-white',
    green: 'bg-gradient-to-br from-[#4A7A4A] to-[#3A5F3A] text-white',
    ochre: 'bg-gradient-to-br from-[#F4E4DA] to-[#EBD3C2] text-[#2A2A2A]',
    stone: 'bg-gradient-to-br from-[#9A8A7E] to-[#7A6E62] text-white',
    danger: 'bg-gradient-to-br from-[#B84A4A] to-[#8E3838] text-white',
  };
  const iconColor = tone === 'ochre' ? 'text-[#7B2C3B]' : 'text-white/80';
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={!onClick}
      className={`text-left rounded-2xl p-4 shadow-sm ${tones[tone]} ${onClick ? 'hover:shadow-md transition-shadow cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-start gap-2">
        <Icon className={`w-5 h-5 ${iconColor}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] uppercase tracking-wider ${tone === 'ochre' ? 'text-[#9A6E4C]' : 'text-white/70'}`}>{label}</p>
          <p className="font-serif text-4xl mt-1">{value}</p>
          {sub && <p className={`text-[11px] mt-1 ${tone === 'ochre' ? 'text-[#6A6A6A]' : 'text-white/80'}`}>{sub}</p>}
        </div>
      </div>
    </button>
  );
}



// Integration partners panel — the couple sees at a glance which of the
// third-party services (Postmark, Quo, LLM, Storage) are wired up and how
// the last 30 days of deliveries have looked. Read-only.
function IntegrationHealthPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get('/dashboard/integrations');
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return (
    <div data-testid="integrations-panel" className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5 flex items-center gap-2 text-sm text-[#6A6A6A]">
      <Loader2 className="w-4 h-4 animate-spin" /> Checking integration health…
    </div>
  );
  if (error) return (
    <div data-testid="integrations-panel" className="soft-card bg-white rounded-2xl border border-red-200 p-5 text-sm text-red-700">
      Couldn&apos;t load integration health: {error}
    </div>
  );

  const STATUS_STYLES = {
    healthy:        { icon: Wifi,    dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Connected · healthy' },
    degraded:       { icon: AlertTriangle, dot: 'bg-amber-500',  pill: 'bg-amber-50 text-amber-700 border-amber-200',   label: 'Connected · issues' },
    not_configured: { icon: WifiOff, dot: 'bg-stone-400',  pill: 'bg-stone-100 text-stone-600 border-stone-200',   label: 'Not configured' },
  };

  return (
    <section data-testid="integrations-panel" className="soft-card bg-white rounded-2xl border border-[#EFE3D8] p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Plug className="w-4 h-4 text-[#7B2C3B]" />
          <h3 className="font-serif text-xl text-[#2A2A2A]">Integration partners</h3>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[#6A6A6A]">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> {data.summary.healthy} healthy</span>
          {data.summary.degraded > 0 && (
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> {data.summary.degraded} degraded</span>
          )}
          {data.summary.not_configured > 0 && (
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-stone-400" /> {data.summary.not_configured} not configured</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {data.integrations.map((integ) => {
          const s = STATUS_STYLES[integ.status] || STATUS_STYLES.not_configured;
          const Icon = s.icon;
          return (
            <div
              key={integ.id}
              data-testid={`integration-${integ.id}`}
              className="rounded-xl border border-[#EFE3D8] bg-[#FBF7F2]/60 p-4"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#9A8A7E]">{integ.category}</p>
                  <p className="font-serif text-lg text-[#2A2A2A]">{integ.name}</p>
                </div>
                <span className={`inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full border ${s.pill}`}>
                  <Icon className="w-3 h-3" /> {integ.subLabel || s.label}
                </span>
              </div>

              <p className="text-[11px] text-[#6A6A6A] mb-3 leading-relaxed">{integ.note}</p>

              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="flex items-center gap-1.5">
                  <KeyRound className="w-3 h-3 text-[#9A8A7E]" />
                  <span className="text-[#6A6A6A]">
                    API key: {integ.keyPresent ? (
                      <span className="font-mono text-[#2A2A2A]">{integ.keyMasked}</span>
                    ) : (
                      <span className="text-amber-700">Missing</span>
                    )}
                  </span>
                </div>
                {integ.stats?.lastActivityAt !== undefined && (
                  <div className="text-[#6A6A6A]">
                    Last activity: <span className="text-[#2A2A2A]">{integ.stats.lastActivityAt ? new Date(integ.stats.lastActivityAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'none yet'}</span>
                  </div>
                )}
                {typeof integ.stats?.sent30d === 'number' && (
                  <div className="text-[#6A6A6A]">Sent (30d): <span className="text-[#2A2A2A]">{integ.stats.sent30d}</span></div>
                )}
                {typeof integ.stats?.delivered30d === 'number' && (
                  <div className="text-[#6A6A6A]">Delivered: <span className="text-emerald-700">{integ.stats.delivered30d}</span></div>
                )}
                {typeof integ.stats?.bounced30d === 'number' && integ.stats.bounced30d > 0 && (
                  <div className="text-[#6A6A6A]">Bounced: <span className="text-red-700">{integ.stats.bounced30d}</span></div>
                )}
                {typeof integ.stats?.failed30d === 'number' && integ.stats.failed30d > 0 && (
                  <div className="text-[#6A6A6A]">Failed: <span className="text-red-700">{integ.stats.failed30d}</span></div>
                )}
                {integ.stats?.inboundWebhookConfigured !== undefined && (
                  <div className="text-[#6A6A6A]">
                    Inbound webhook: {integ.stats.inboundWebhookConfigured
                      ? <span className="text-emerald-700">Configured</span>
                      : <span className="text-amber-700">Not set</span>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
